import React, { useState } from "react";
import styles from "./CygnusFormBuilderToolbar.module.css";
import { FieldProps, FieldVariant, SectionLayout, SectionVariant } from "../../../InputFieldsController/InputFieldsController.utils";
import FieldLabelStyler from "./components/FieldLabelStyler/FieldLabelStyler";
import { Accordion } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import FieldStyler from "./components/FieldStyler/FieldStyler";
import FieldConfigurator from "./components/FieldConfigurator/FieldConfigurator";
import {
  CustomAccordionToggleProps,
  CygnusFormBuilderToolbarProps,
} from "./CygnusFormBuilderToolbar.utils";
import FieldValidator from "./components/FieldValidator/FieldValidator";
import SectionStyler from "./components/SectionStyler/FieldStyler";
import { FormSettings } from "../../CygnusFormBuilder.utils";

const CustomAccordionToggle: React.FC<CustomAccordionToggleProps> = ({
  children,
  eventKey,
  toggleSection,
  isActive,
}) => {
  switch (eventKey) {
    case "fieldConfig":
      return (
        <div
          onClick={() => toggleSection(eventKey)}
          className={styles.toolbarAccordionHeader}
        >
          <div>{children}</div>
          <Icon.GearFill
            style={{
              transition: "transform 0.3s ease",
              transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </div>
      );

    default:
      return (
        <div
          onClick={() => toggleSection(eventKey)}
          className={styles.toolbarAccordionHeader}
        >
          <div>{children}</div>
          <Icon.PlusLg
            style={{
              transition: "transform 0.3s ease",
              transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </div>
      );
  }
};


const FIELD_VARIANT_OPTIONS: { value: FieldVariant; label: string; inputPreview: React.CSSProperties }[] = [
  { value: "default",  label: "Default",  inputPreview: { backgroundColor: "#f1f3f4", borderBottom: "2px solid #9aa0a6", borderRadius: 0 } },
  { value: "outlined", label: "Outlined", inputPreview: { backgroundColor: "#fff", border: "1.5px solid #d1d5db", borderRadius: 6 } },
  { value: "filled",   label: "Filled",   inputPreview: { backgroundColor: "#eef1f6", borderBottom: "2px solid #4a90d9", borderRadius: 8 } },
  { value: "underline",label: "Underline",inputPreview: { backgroundColor: "transparent", borderBottom: "1.5px solid #9ca3af", borderRadius: 0 } },
];

const SECTION_VARIANT_OPTIONS: { value: SectionVariant; label: string; preview: React.CSSProperties }[] = [
  { value: "card", label: "Card", preview: { backgroundColor: "#ffffff", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } },
  { value: "flat", label: "Flat", preview: { backgroundColor: "transparent", borderTop: "2px solid #6c63ff", borderRadius: 0 } },
];

export type ThemePreset = {
  name: string;
  label: string;
  description: string;
  fieldVariant: FieldVariant;
  sectionVariant: SectionVariant;
  sectionLayout: SectionLayout;
  sectionStyles: {
    backgroundColor: string;
    borderColor: string;
    titleColor: string;
  };
};

export const THEME_PRESETS: ThemePreset[] = [
  {
    name: "branded",
    label: "Branded",
    description: "Firm's color palette",
    fieldVariant: "outlined",
    sectionVariant: "card",
    sectionLayout: "banner",
    sectionStyles: {
      backgroundColor: "color-mix(in srgb, var(--theme1_tertiary) 15%, white)",
      borderColor: "var(--theme1_tertiary)",
      titleColor: "var(--theme1_primary)",
    },
  },
  {
    name: "clean",
    label: "Clean",
    description: "Minimal, no styling",
    fieldVariant: "underline",
    sectionVariant: "flat",
    sectionLayout: "banner",
    sectionStyles: {
      backgroundColor: "#f3f4f6",
      borderColor: "#e5e7eb",
      titleColor: "#374151",
    },
  },
];

const getFirmColors = (): { primary: string; tertiary: string } | null => {
  try {
    const raw = localStorage.getItem("theme");
    if (!raw) return null;
    const t = JSON.parse(raw);
    const c = t?.colors;
    if (!c?.tertiary) return null;
    return { primary: c.primary ?? "#333333", tertiary: c.tertiary };
  } catch {
    return null;
  }
};

const tintHex = (hex: string, opacity = 0.15): string => {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return hex;
  const r = Math.round(parseInt(clean.slice(0, 2), 16) * opacity + 255 * (1 - opacity));
  const g = Math.round(parseInt(clean.slice(2, 4), 16) * opacity + 255 * (1 - opacity));
  const b = Math.round(parseInt(clean.slice(4, 6), 16) * opacity + 255 * (1 - opacity));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

const CygnusFormBuilderToolbar: React.FC<CygnusFormBuilderToolbarProps> = ({
  selectedField,
  updateField,
  removeField,
  onLaunchSimulation,
  onManageSystemFields,
  unresolvedSystemFieldCount = 0,
  templateStatus = "Draft",
  fieldOptions,
  formSettings,
  updateFormSettings,
  applyTheme,
}) => {
  const [openSections, setOpenSections] = useState<string[]>(["formStyle", "fieldConfig"]);
  const toggleSection = (key: string) => {
    setOpenSections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const expandAll = () =>
    setOpenSections([
      "formStyle",
      "intakeRules",
      "fieldConfig",
      "fieldStyles",
      "labelStyles",
      "validation",
    ]);
  const collapseAll = () => setOpenSections([]);

  const isFinal = templateStatus === "Final";
  const firmColors = getFirmColors();

  const isActiveTheme = (preset: ThemePreset) =>
    formSettings?.themePreset
      ? formSettings.themePreset === preset.name
      : false;

  const update = (partial: Partial<FieldProps>) => {
    if (!selectedField) return;
    updateField({ ...selectedField, ...partial });
  };

  return (
    <>
      <div className={styles.toolbarContainer}>
        <div className={styles.toolBarHeaderButtons}>
          <div className={styles.primaryToolbarActions}>
            <button
              type="button"
              className={styles.toolbarIconButton}
              onClick={onLaunchSimulation}
              title="Preview form"
            >
              <Icon.PlayCircleFill />
            </button>
            {onManageSystemFields && (
              <button
                type="button"
                className={styles.toolbarIconButton}
                onClick={onManageSystemFields}
                title="Manage system fields"
              >
                <Icon.DatabaseAdd />
                {unresolvedSystemFieldCount > 0 && (
                  <span className={styles.toolbarIconBadge}>
                    {unresolvedSystemFieldCount}
                  </span>
                )}
              </button>
            )}
          </div>
          <div>
            <Icon.ArrowsExpand
              className={styles.expandCollapseButton}
              onClick={expandAll}
            />
            <Icon.ArrowsCollapse
              className={styles.expandCollapseButton}
              onClick={collapseAll}
            />
          </div>
        </div>
        <Accordion
          activeKey={openSections}
          className={styles.toolbarAccordion}
          alwaysOpen
        >
          {/* FORM STYLE — always visible, form-level setting */}
          <Accordion.Item
            className={styles.toolbarAccordionItem}
            eventKey="formStyle"
          >
            <CustomAccordionToggle
              eventKey="formStyle"
              toggleSection={toggleSection}
              isActive={openSections.includes("formStyle")}
            >
              Form Style
            </CustomAccordionToggle>
            <Accordion.Body className={styles.toolbarAccordionBody}>
              <div className={styles.formGroup}>
                <label>Theme</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 4 }}>
                  {THEME_PRESETS.map((preset) => {
                    const active = isActiveTheme(preset);
                    const isBranded = preset.name === "branded";
                    const swatchHeader = isBranded
                      ? (firmColors ? tintHex(firmColors.tertiary, 0.15) : "color-mix(in srgb, var(--theme1_tertiary) 15%, white)")
                      : "#f3f4f6";
                    const swatchAccent = isBranded
                      ? (firmColors?.tertiary ?? "var(--theme1_tertiary)")
                      : "#9ca3af";
                    const activeBorder = isBranded
                      ? (firmColors?.tertiary ?? "var(--theme1_tertiary)")
                      : "#9ca3af";
                    return (
                      <div
                        key={preset.name}
                        onClick={() => !isFinal && applyTheme?.(preset.name)}
                        title={preset.description}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 6,
                          border: active ? `2px solid ${activeBorder}` : "1.5px solid #e0e0e0",
                          overflow: "hidden",
                          cursor: isFinal ? "not-allowed" : "pointer",
                          opacity: isFinal ? 0.6 : 1,
                          transition: "border-color 0.15s",
                          position: "relative",
                        }}
                      >
                        {active && (
                          <div style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor: activeBorder,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                          }}>
                            <Icon.CheckLg style={{ fontSize: 8, color: "#fff" }} />
                          </div>
                        )}
                        <div style={{ height: 20, backgroundColor: swatchHeader }} />
                        <div style={{
                          height: 22,
                          backgroundColor: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                          paddingRight: 6,
                          borderLeft: `3px solid ${swatchAccent}`,
                        }}>
                          {[50, 35, 25].map((w, i) => (
                            <div key={i} style={{ height: 3, width: `${w}%`, backgroundColor: "#e5e7eb", borderRadius: 2, marginLeft: i === 0 ? 5 : 0 }} />
                          ))}
                        </div>
                        <div style={{ padding: "3px 6px 5px", backgroundColor: "#fafafa" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: active ? activeBorder : "#333", lineHeight: 1.3 }}>
                            {preset.label}
                          </div>
                          <div style={{ fontSize: 7, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
                            {preset.description}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.formGroup} style={{ marginTop: 10 }}>
                <label>Field Variant</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, marginTop: 4 }}>
                  {FIELD_VARIANT_OPTIONS.map((opt) => {
                    const active = (formSettings?.fieldVariant ?? "default") === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => !isFinal && updateFormSettings?.({ ...formSettings, fieldVariant: opt.value } as any)}
                        title={opt.label}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 3,
                          padding: "5px 3px",
                          borderRadius: 5,
                          border: active ? "2px solid var(--theme1_tertiary)" : "2px solid #e0e0e0",
                          backgroundColor: active ? "rgba(0,0,0,0.04)" : "#fafafa",
                          cursor: isFinal ? "not-allowed" : "pointer",
                          opacity: isFinal ? 0.6 : 1,
                          transition: "border-color 0.15s, background-color 0.15s",
                        }}
                      >
                        <div style={{ ...opt.inputPreview, height: 5, width: "80%" }} />
                        <div style={{ fontSize: 7, fontWeight: 600, color: active ? "var(--theme1_tertiary)" : "#666", textAlign: "center", lineHeight: 1.2 }}>{opt.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.formGroup} style={{ marginTop: 10 }}>
                <label>Section Variant</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 4, marginTop: 4 }}>
                  {SECTION_VARIANT_OPTIONS.map((opt) => {
                    const active = (formSettings?.sectionVariant ?? "default") === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => !isFinal && updateFormSettings?.({ ...formSettings, sectionVariant: opt.value } as any)}
                        title={opt.label}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 3,
                          padding: "5px 3px",
                          borderRadius: 5,
                          border: active ? "2px solid var(--theme1_tertiary)" : "2px solid #e0e0e0",
                          backgroundColor: active ? "rgba(0,0,0,0.04)" : "#fafafa",
                          cursor: isFinal ? "not-allowed" : "pointer",
                          opacity: isFinal ? 0.6 : 1,
                          transition: "border-color 0.15s, background-color 0.15s",
                        }}
                      >
                        <div style={{ ...opt.preview, width: "100%", height: 20, marginBottom: 1 }} />
                        <div style={{ fontSize: 7, fontWeight: 600, color: active ? "var(--theme1_tertiary)" : "#666", textAlign: "center", lineHeight: 1.2 }}>{opt.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* INTAKE RULES - form-level setting */}
          <Accordion.Item
            className={styles.toolbarAccordionItem}
            eventKey="intakeRules"
          >
            <CustomAccordionToggle
              eventKey="intakeRules"
              toggleSection={toggleSection}
              isActive={openSections.includes("intakeRules")}
            >
              Intake Rules
            </CustomAccordionToggle>
            <Accordion.Body className={styles.toolbarAccordionBody}>
              <div className={styles.formGroup}>
                <textarea
                  className={styles.intakeRulesTextarea}
                  value={formSettings?.intakeRules ?? ""}
                  onChange={(event) =>
                    updateFormSettings?.({
                      ...formSettings,
                      intakeRules: event.target.value,
                    })
                  }
                  placeholder="Enter intake rules"
                  disabled={isFinal}
                />
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* FIELD CONFIG ONLY WHEN FIELD IS SELECTED */}
          <Accordion.Item
            className={styles.toolbarAccordionItem}
            eventKey="fieldConfig"
          >
            <CustomAccordionToggle
              eventKey="fieldConfig"
              toggleSection={toggleSection}
              isActive={openSections.includes("fieldConfig")}
            >
              Field Configuration
            </CustomAccordionToggle>
            <Accordion.Body className={styles.toolbarAccordionBody}>
              {selectedField ? (
                <FieldConfigurator
                  isFinal={isFinal}
                  fieldOptions={fieldOptions ?? []}
                  selectedField={selectedField}
                  updateField={update}
                  removeField={removeField}
                />
              ) : (
                <div className={styles.placeholder}>
                  Select a field from the form to configure
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>

          {selectedField?.sectionStart && (
            <Accordion.Item
              className={styles.toolbarAccordionItem}
              eventKey="sectionSettings"
            >
              <CustomAccordionToggle
                eventKey="sectionSettings"
                toggleSection={toggleSection}
                isActive={openSections.includes("sectionSettings")}
              >
                Section Settings
              </CustomAccordionToggle>
              <Accordion.Body className={styles.toolbarAccordionBody}>
                <SectionStyler
                  selectedField={selectedField}
                  update={update}
                  sectionVariant={formSettings?.sectionVariant}
                />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* VALIDATION ONLY WHEN FIELD IS SELECTED */}
          <Accordion.Item
            className={styles.toolbarAccordionItem}
            eventKey="validation"
          >
            <CustomAccordionToggle
              eventKey="validation"
              toggleSection={toggleSection}
              isActive={openSections.includes("validation")}
            >
              Validation
            </CustomAccordionToggle>
            <Accordion.Body className={styles.toolbarAccordionBody}>
              {selectedField ? (
                <FieldValidator
                  selectedField={selectedField}
                  updateField={update}
                />
              ) : (
                <div className={styles.placeholder}>
                  Select a field from the form to configure validations
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>

          {/* FIELD STYLES ONLY WHEN FIELD IS SELECTED */}
          <Accordion.Item
            className={styles.toolbarAccordionItem}
            eventKey="fieldStyles"
          >
            <CustomAccordionToggle
              eventKey="fieldStyles"
              toggleSection={toggleSection}
              isActive={openSections.includes("fieldStyles")}
            >
              Field Styles
            </CustomAccordionToggle>
            <Accordion.Body className={styles.toolbarAccordionBody}>
              {/* Margins */}
              {selectedField ? (
                <FieldStyler
                  isFinal={isFinal}
                  selectedField={selectedField}
                  updateField={update}
                />
              ) : (
                <div className={styles.placeholder}>
                  Select a field from the form to edit the field styles
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>

          {/* LABEL STYLES ONLY WHEN FIELD IS SELECTED */}
          <Accordion.Item
            className={styles.toolbarAccordionItem}
            eventKey="labelStyles"
          >
            <CustomAccordionToggle
              eventKey="labelStyles"
              toggleSection={toggleSection}
              isActive={openSections.includes("labelStyles")}
            >
              Label Styles
            </CustomAccordionToggle>
            <Accordion.Body className={styles.toolbarAccordionBody}>
              {/* Label Styling */}
              {selectedField ? (
                <FieldLabelStyler
                  selectedField={selectedField}
                  updateField={update}
                />
              ) : (
                <div className={styles.placeholder}>
                  Select a field from the form to edit the label styles
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </>
  );
};

export default CygnusFormBuilderToolbar;
